import CategorySelect from "@/components/products/CategorySelect";
import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, Trash2, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import PurchaseHistory from "@/components/products/PurchaseHistory";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { formatNumber, formatPct, imgSrc } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import BarcodeChangeDialog from "@/components/products/BarcodeChangeDialog";

const EMPTY = { sku: "", barcode: "", name: "", brand: "", category: "", unit: "Adet", koli_ici: 1, image_url: "", purchase_price: 0, purchase_currency: "TRY",
  real_cost: 0, sale_price: 0, vat_rate: 20, min_stock: 0, raf: "", warehouse_id: "", supplier_id: "", origin: "Türkiye", is_active: true };
const COST_KEYS = ["purchase_price", "purchase_currency", "real_cost"];
const NUM_KEYS = ["koli_ici", "purchase_price", "real_cost", "sale_price", "vat_rate", "min_stock"];

const F = ({ label, children, span = 1 }) => <div className={`space-y-1 ${span === 2 ? "col-span-2" : ""}`}><Label className="text-[11px] text-slate-500">{label}</Label>{children}</div>;

function Pick({ value, onChange, options, disabled, testid }) {
  return (
    <Select value={String(value ?? "")} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-9" data-testid={testid}><SelectValue placeholder="Seçin" /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function ImageField({ url, disabled, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/product-image", fd);
      onChange(data.url);
      toast.success("Görsel yüklendi");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <div className="row-span-3 flex flex-col gap-2" data-testid="product-image-field">
      <div className="h-36 w-36 rounded border border-slate-200 bg-slate-50 grid place-items-center overflow-hidden">
        {url ? <img src={imgSrc(url)} alt="" className="h-full w-full object-cover" data-testid="product-image-preview" /> : <ImageOff className="h-6 w-6 text-slate-300" />}
      </div>
      {!disabled && (
        <>
          <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="product-image-input" />
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => ref.current?.click()} data-testid="product-image-upload-button">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}Görsel Yükle
          </Button>
        </>
      )}
    </div>
  );
}

export default function ProductDialog({ productId, onClose, onSaved }) {
  const { can, flag, user } = useAuth();
  const [chg, setChg] = useState(false);
  const isNew = productId === "new";
  const [opts, setOpts] = useState(null);
  const [f, setF] = useState(isNew ? EMPTY : null);
  const [orig, setOrig] = useState(null);
  const [busy, setBusy] = useState(false);
  const showCost = flag("cost_view");
  const readOnly = isNew ? !can("urunler", "create") : !can("urunler", "edit");

  useEffect(() => {
    api.get("/products/form-options").then(({ data }) => { setOpts(data); if (isNew) setF((p) => ({ ...p, warehouse_id: data.warehouses[0]?.id || "" })); });
    if (!isNew) api.get(`/products/${productId}`).then(({ data }) => { setF({ ...EMPTY, ...data }); setOrig(data); }).catch((e) => { toast.error(apiError(e)); onClose(); });
  }, [productId, isNew, onClose]);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = Object.fromEntries(Object.keys(EMPTY).filter((k) => showCost || !COST_KEYS.includes(k)).map((k) => [k, NUM_KEYS.includes(k) ? Number(f[k]) : f[k]]));
    try {
      if (isNew) { const { data } = await api.post("/products", body); if (data.barcode_info) toast.success(data.barcode_info, { duration: 6000 }); }
      else await api.put(`/products/${productId}`, Object.fromEntries(Object.entries(body).filter(([k, v]) => String(v) !== String(orig[k]))));
      toast.success(isNew ? "Ürün oluşturuldu" : "Ürün güncellendi");
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm("Ürün kalıcı olarak silinsin mi?")) return;
    try { await api.delete(`/products/${productId}`); toast.success("Ürün silindi"); onSaved(); } catch (err) { toast.error(apiError(err)); }
  };

  const ready = opts && f;
  const inp = (k, props = {}) => <Input className="h-9" value={f[k] ?? ""} onChange={set(k)} disabled={readOnly} data-testid={`product-form-${k}`} {...props} />;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="product-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Yeni Ürün Kartı" : `Ürün Kartı · ${f?.sku || ""}`}</DialogTitle></DialogHeader>
        {!ready ? <div className="h-64 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-[auto_1fr_1fr] gap-3">
              <ImageField url={f.image_url} disabled={readOnly} onChange={set("image_url")} />
              <F label="SKU">{inp("sku", { required: true })}</F>
              <F label="Barkod (EAN, opsiyonel)"><div className="flex gap-1">{inp("barcode", { pattern: "\\d{8,14}", title: "8–14 hane; boş bırakılabilir" })}
                {!readOnly && !f.barcode && can("urunler", "edit") && <Button type="button" size="sm" variant="outline" className="h-9 shrink-0"
                  onClick={async () => { try { const { data } = await api.get("/barcodes/new"); set("barcode")(data.barcode); toast.success("Dahili EAN-13 üretildi; kaydedince atanır"); } catch (err) { toast.error(apiError(err)); } }}
                  data-testid="product-form-barcode-generate">Barkod Üret</Button>}
                {!readOnly && productId && f.barcode && user?.is_super && <Button type="button" size="sm" variant="outline" className="h-9 shrink-0" onClick={() => setChg(true)} data-testid="product-form-barcode-change">Barkodu Değiştir</Button>}</div></F>
              {chg && <BarcodeChangeDialog productId={productId} current={f.barcode} onClose={() => setChg(false)} onDone={(b) => { set("barcode")(b); setChg(false); }} />}
              <div className="col-span-2"><F label="Ürün Adı" span={2}>{inp("name", { required: true })}</F></div>
              <F label="Marka">{inp("brand", { required: true, list: "brand-list" })}</F>
              <F label="Kategori"><CategorySelect value={f.category || ""} original={orig?.category} onChange={set("category")} /></F>
            </div>
            <datalist id="brand-list">{opts.brands.map((b) => <option key={b} value={b} />)}</datalist>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <F label="Birim">{inp("unit")}</F>
              <F label="Koli İçi">{inp("koli_ici", { type: "number", min: 1, required: true })}</F>
              <F label="Min. Stok">{inp("min_stock", { type: "number", min: 0, required: true })}</F>
              <F label="Raf">{inp("raf")}</F>
              <F label="Depo"><Pick value={f.warehouse_id} onChange={set("warehouse_id")} disabled={readOnly} testid="product-form-warehouse" options={opts.warehouses.map((w) => ({ value: w.id, label: w.name }))} /></F>
              <div className="col-span-2"><F label="Varsayılan Alış Carisi (opsiyonel)"><Pick value={f.supplier_id || "none"} onChange={(v) => set("supplier_id")(v === "none" ? "" : v)} disabled={readOnly} testid="product-form-supplier" options={[{ value: "none", label: "— Yok —" }, ...opts.suppliers.map((s) => ({ value: s.id, label: `${s.name} (${s.currency})` }))]} /></F></div>
              <F label="Menşei">{inp("origin")}</F>
            </div>
            <div className="rounded border border-slate-200 p-3 bg-slate-50/60">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Fiyatlandırma</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {showCost && <F label="Alış Fiyatı">{inp("purchase_price", { type: "number", step: "0.01", min: 0 })}</F>}
                {showCost && <F label="Alış Döviz"><Pick value={f.purchase_currency} onChange={set("purchase_currency")} disabled={readOnly} testid="product-form-purchase_currency" options={["TRY", "USD", "EUR", "CNY"].map((c) => ({ value: c, label: c }))} /></F>}
                {showCost && <F label="Gerçek Maliyet (₺)">{inp("real_cost", { type: "number", step: "0.01", min: 0 })}</F>}
                <F label="Satış Fiyatı (₺, KDV hariç)">{inp("sale_price", { type: "number", step: "0.01", min: 0, required: true })}</F>
                <F label="KDV %"><Pick value={f.vat_rate} onChange={(v) => set("vat_rate")(Number(v))} disabled={readOnly} testid="product-form-vat_rate" options={[0, 1, 10, 20].map((v) => ({ value: v, label: `%${v}` }))} /></F>
              </div>
              {!isNew && orig && (
                <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-500" data-testid="product-stock-summary">
                  <span>Fiziki: <b className="font-mono text-slate-800">{formatNumber(orig.physical_qty)}</b></span>
                  <span>Rezerve: <b className="font-mono text-slate-800">{formatNumber(orig.reserved_qty)}</b></span>
                  <span>Kullanılabilir: <b className="font-mono text-slate-800">{formatNumber(orig.available_qty)}</b></span>
                  <span>Yolda: <b className="font-mono text-slate-800" data-testid="product-yolda">{formatNumber(orig.pipeline?.yolda ?? orig.in_transit_qty)}</b></span>
                  <span>Gümrükte: <b className="font-mono text-violet-700" data-testid="product-gumrukte">{formatNumber(orig.pipeline?.gumrukte || 0)}</b></span>
                  <span>Toplam Potansiyel: <b className="font-mono text-slate-800" data-testid="product-potential">{formatNumber(orig.pipeline?.toplam_potansiyel ?? orig.physical_qty)}</b></span>
                  {"margin_pct" in orig && <span>Marj: <b className="font-mono text-emerald-700">{formatPct(orig.margin_pct)}</b></span>}
                </div>
              )}
              {!showCost && <p className="text-[11px] text-slate-400 mt-2">Maliyet alanları rolünüz için gizlidir.</p>}
            </div>
            {!isNew && orig && showCost && <PurchaseHistory product={orig} />}
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.is_active} onCheckedChange={set("is_active")} disabled={readOnly} data-testid="product-form-active" />Aktif</label>
            <DialogFooter className="gap-2">
              {!isNew && can("urunler", "delete") && <Button type="button" variant="outline" className="text-rose-600 mr-auto" onClick={remove} data-testid="product-delete-button"><Trash2 className="h-4 w-4 mr-1" />Sil</Button>}
              <Button type="button" variant="outline" onClick={onClose} data-testid="product-form-cancel">Kapat</Button>
              {!readOnly && <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="product-form-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Kaydet</Button>}
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
